/**
 * SA-6 — MSR commission ESTIMATOR (forward-looking "what could I earn").
 *
 * Pure, rate-driven calculator: given the MSR's planned/expected activity counts and the configurable
 * rates (from public.commission_estimator_rates, seeded from the MSR Commission Calculator pay tables),
 * produce an itemized estimate. Kept pure so it's unit-tested directly and runs client-side for instant
 * feedback.
 *
 * IMPORTANT (operator sign-off note): rate VALUES are configurable (edit the rate table). The estimate
 * is a POTENTIAL maximum — it counts both Day 1 and Day 31 conversion commissions; actual Day 31 pay
 * requires the member to still be active at day 31 (handled by the real commission ledger, not this
 * estimator). Definitions of "verified / passive / upstream / assigned account" are the operator's to
 * refine; this calculator just multiplies counts by the configured rates.
 */

export type CommissionEstimatorCategory = "event_pay" | "conversion";

export interface CommissionEstimatorRate {
  rate_key: string;
  label: string;
  amount: number;
  category: CommissionEstimatorCategory;
}

/** MSR-entered activity counts. */
export interface CommissionEstimatorInputs {
  classesWithSale: number;
  gunShowTablesWithSale: number;
  seminarsWithSale: number;
  oneTimeEvents: number;
  verifiedSales: number;
  passiveSales: number;
  leadScans: number;
  upstreamSales: number;
}

export interface EstimateLineItem {
  key: string;
  label: string;
  quantity: number;
  unitAmount: number;
  subtotal: number;
}

export interface CommissionEstimate {
  eventPay: EstimateLineItem[];
  conversion: EstimateLineItem[];
  eventPayTotal: number;
  conversionTotal: number;
  total: number;
}

export const EMPTY_ESTIMATOR_INPUTS: CommissionEstimatorInputs = {
  classesWithSale: 0,
  gunShowTablesWithSale: 0,
  seminarsWithSale: 0,
  oneTimeEvents: 0,
  verifiedSales: 0,
  passiveSales: 0,
  leadScans: 0,
  upstreamSales: 0,
};

const round2 = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;

/** Non-negative, finite integer-ish count (NaN/negative/Infinity → 0). */
const count = (n: number | undefined | null): number =>
  typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;

function rateMap(rates: CommissionEstimatorRate[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const r of rates) {
    // Clamp to >= 0: a bad operator edit (rates are SQL-editable) must never
    // produce a negative estimated commission. (DB also has CHECK (amount>=0).)
    m[r.rate_key] =
      typeof r.amount === "number" && Number.isFinite(r.amount) && r.amount > 0
        ? r.amount
        : 0;
  }
  return m;
}

const line = (
  key: string,
  label: string,
  quantity: number,
  unitAmount: number,
): EstimateLineItem => ({
  key,
  label,
  quantity,
  unitAmount: round2(unitAmount),
  subtotal: round2(quantity * unitAmount),
});

/**
 * Compute the estimate. Missing rate keys default to 0 (so a partially-configured rate table degrades
 * gracefully rather than throwing). Day 1 + Day 31 are summed per conversion line (potential max).
 */
export function estimateCommission(
  inputs: CommissionEstimatorInputs,
  rates: CommissionEstimatorRate[],
): CommissionEstimate {
  const r = rateMap(rates);

  const eventPay: EstimateLineItem[] = [
    line(
      "class_base",
      "Classes (with a sale)",
      count(inputs.classesWithSale),
      r.class_base ?? 0,
    ),
    line(
      "gun_show_tables_base",
      "Gun shows / tables (with a sale)",
      count(inputs.gunShowTablesWithSale),
      r.gun_show_tables_base ?? 0,
    ),
    line(
      "seminar_base",
      "Seminars (with a sale)",
      count(inputs.seminarsWithSale),
      r.seminar_base ?? 0,
    ),
    line(
      "one_time_event_base",
      "One-time events",
      count(inputs.oneTimeEvents),
      r.one_time_event_base ?? 0,
    ),
  ];

  const conversion: EstimateLineItem[] = [
    line(
      "verified",
      "Verified sales (Day 1 + Day 31)",
      count(inputs.verifiedSales),
      (r.verified_day1 ?? 0) + (r.verified_day31 ?? 0),
    ),
    line(
      "passive",
      "Passive sales — assigned accounts (Day 1 + Day 31)",
      count(inputs.passiveSales),
      (r.passive_day1 ?? 0) + (r.passive_day31 ?? 0),
    ),
    line(
      "lead_scan",
      "Lead scans — assigned accounts",
      count(inputs.leadScans),
      r.lead_scan ?? 0,
    ),
    line(
      "upstream",
      "Upstream — assigned accounts (Day 1 + Day 31)",
      count(inputs.upstreamSales),
      (r.upstream_day1 ?? 0) + (r.upstream_day31 ?? 0),
    ),
  ];

  const eventPayTotal = round2(eventPay.reduce((s, l) => s + l.subtotal, 0));
  const conversionTotal = round2(
    conversion.reduce((s, l) => s + l.subtotal, 0),
  );

  return {
    eventPay,
    conversion,
    eventPayTotal,
    conversionTotal,
    total: round2(eventPayTotal + conversionTotal),
  };
}
